import * as Sentry from "@sentry/react";
import { User, UserManager, WebStorageStateStore } from "oidc-client-ts";
import { getLanguageFromPath } from "plos/src/utils/language";
import React, { ReactNode } from "react";
import { AuthProvider } from "react-oidc-context";
import { getCookie } from "shared-utils/src/cookies";
import {
  authErrorFields,
  isCopilotAPI,
  isResourceOurAPI,
  registerAuthEventLogging,
  setHeader,
} from "./helpers";

// Set by the apps in a webview to facilitate seamless login
const appCookieName = "ACCESS_TOKEN";
const appCookieDomain = import.meta.env.VITE_APP_COOKIE_DOMAIN;

// Apply a random time between 5 and 3 minutes. This decreases the chances of there being
// concurrent attempts to refresh the token across multiple tabs.
const accessTokenExpiryTime = Math.floor(Math.random() * 121) + 180;

export const userManager = new UserManager({
  authority: import.meta.env.VITE_OIDC_AUTHORITY,
  client_id: import.meta.env.VITE_OIDC_CLIENT_ID,
  redirect_uri: `${window.location.origin}/`,
  scope: "openid profile email offline_access p1:update:user consent interest", // offline_access is required to refresh tokens
  post_logout_redirect_uri: `${window.location.origin}/`,
  // set to false so listeners below are registered before the renew handler.
  // true was registering that handler beforehand, and the addAccessTokenExpiring
  // log would only run after the renewal was successfully completed.
  // userManager.startSilentRenew() below is the same as automaticSilentRenew: true
  automaticSilentRenew: false,
  accessTokenExpiringNotificationTimeInSeconds: accessTokenExpiryTime,
  userStore: new WebStorageStateStore({ store: window.localStorage }),
});

export const u13userManager = new UserManager({
  authority: import.meta.env.VITE_OIDC_AUTHORITY,
  client_id: import.meta.env.VITE_OIDC_CLIENT_U13_ID,
  redirect_uri: `${window.location.origin}/`,
  scope: "openid profile email offline_access p1:update:user consent interest", // offline_access is required to refresh tokens
  post_logout_redirect_uri: `${window.location.origin}/`,
  automaticSilentRenew: false,
  accessTokenExpiringNotificationTimeInSeconds: accessTokenExpiryTime,
  userStore: new WebStorageStateStore({ store: window.localStorage }),
});

registerAuthEventLogging(userManager);

userManager.startSilentRenew();

export const getRequestLanguage = () =>
  getLanguageFromPath(window.location.pathname);

const handleAuthIssue = async (reason: string, error?: unknown) => {
  Sentry.logger.warn(
    "Clearing session: Soft logout, remove oidc user from localStorage and user redirected to logged out page",
    {
      event_type: "soft_logout",
      reason,
      ...authErrorFields(error),
    },
  );
  await userManager.removeUser();
  document.cookie = `${appCookieName}=; max-age=0; path=/; domain=${appCookieDomain}; samesite=strict; secure`;
  window.location.href = "/";
  return null;
};

// Ensures getUser only runs one instance at a time and returns the same promise to all callers
let getUserPromise: Promise<User | null> | null = null;

export const getUser = async () => {
  if (getUserPromise) {
    return getUserPromise;
  }
  getUserPromise = (async () => {
    let user = await userManager.getUser();
    Sentry.setAttribute("sso_id", user?.profile.sub);
    if (user) {
      // Scope validation
      // const granted = new Set(user.scope?.split(" "));
      // const expectedScopes = userManager.settings.scope
      //   .split(" ")
      //   .filter((scope) => scope !== "offline_access");
      // const missing = expectedScopes.filter((s) => !granted.has(s));
      // if (missing.length) {
      //   Sentry.logger.info(
      //     "Scope mismatch, oidc user removed from localStorage and login retried",
      //     {
      //       event_type: "scope_mismatch",
      //     },
      //   );
      //   await userManager.removeUser();
      //   await userManager.signinRedirect();
      //   return null;
      // }
      // If renewal has failed for some reason then try here
      if (
        user.expired ||
        (user.expires_in && user.expires_in < accessTokenExpiryTime - 5)
      ) {
        // Manually trigger silent renewal if an expiring or expired token exists at this point
        // (shouldn't normally happen with oidc auto-renewal)
        Sentry.logger.info("Manual silent token renewal", {
          event_type: "manual_silent_renewal",
          expired: user.expired,
          expires_in: user.expires_in,
          renewal_source: "getUser",
        });
        try {
          user = await userManager.signinSilent();
        } catch (err) {
          await handleAuthIssue("silent_renewal_failed", err);
          return null;
        }
      }
    }
    return user;
  })();

  try {
    return await getUserPromise;
  } finally {
    getUserPromise = null;
  }
};

// Monkey patch 'fetch' to add the authorization header to API requests
const { fetch: originalFetch } = window;
window.fetch = async (...args) => {
  const [resource, config = {}] = args;

  const ourAPI = isResourceOurAPI(resource);
  if (ourAPI) {
    setHeader(resource, config, "X-API-Language", getRequestLanguage());

    let accessToken;

    // First try and get an access token from a user we have logged in
    const user = await getUser();
    if (user) {
      accessToken = user.access_token;
    }

    // Then see if we can get one from a Cookie (app webview)
    if (!accessToken && document.cookie) {
      accessToken = getCookie(appCookieName);
    }

    // If we have one then pass it in the appropriate header. We have to jump
    // thoough a few hoops to do this due to various nuances of fetch args
    if (accessToken) {
      const bearer = `Bearer ${accessToken}`;
      setHeader(resource, config, "X-API-Authorization", bearer);
    }
  }

  if (isCopilotAPI(resource)) {
    const user = await getUser();
    if (user) {
      setHeader(
        resource,
        config,
        "Authorization",
        `Bearer ${user.access_token}`,
      );
    }
  }

  const response = await originalFetch(resource, config);
  if (ourAPI) {
    if ([401, 403].includes(response.status)) {
      await handleAuthIssue(`api_${response.status}`);
    }
  }
  return response;
};

interface PingAuthProviderProps {
  children: ReactNode;
}

const PingAuthProvider: React.FC<PingAuthProviderProps> = ({ children }) => {
  const onSigninCallback = async () => {
    window.history.replaceState({}, document.title, window.location.pathname);
  };

  return (
    <AuthProvider userManager={userManager} onSigninCallback={onSigninCallback}>
      {children}
    </AuthProvider>
  );
};

export default PingAuthProvider;
